import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import NewOrderModal from "../../components/NewOrderModal";
import OrderInformation from "./OrderInformation";
import ViewOrderInformation from "./ViewOrderInformation";
import ProcessOrderView from "./ProcessOrderView";
import { Plus, ChevronDown } from "lucide-react";
import { adminServices } from "../../services/AdminServices";
import { toast } from "react-toastify";
const OrderManagement = ({
  initialViewOrderId,
  onConsumedInitialViewOrder
}) => {
  const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);
  const [showOrderInformation, setShowOrderInformation] = useState(false);
  const [showViewOrderInformation, setShowViewOrderInformation] = useState(false);
  const [showProcessOrderView, setShowProcessOrderView] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [ordersData, setOrdersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderSummary, setOrderSummary] = useState({
    pending: 0,
    rejected: 0,
    approved: 0,
    voided: 0,
    processing: 0,
    inTransit: 0,
    partiallyDelivered: 0,
    delivered: 0
  });
  const [merchants, setMerchants] = useState([]);
  const [filters, setFilters] = useState({
    keyword: "",
    merchantId: "",
    orderType: ""
  });
  const [searchInput, setSearchInput] = useState("");
  const [dateRange, setDateRange] = useState({
    from: "",
    to: ""
  });
  const [facility, setFacility] = useState("");
  const [facilities, setFacilities] = useState([{
    value: "",
    label: "Facility"
  }]);
  useEffect(() => {
    if (!initialViewOrderId) return;
    setSelectedOrderId(initialViewOrderId);
    setShowViewOrderInformation(true);
    onConsumedInitialViewOrder?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialViewOrderId]);

  // Fetch orders data
  useEffect(() => {
    fetchOrderSummary();
    fetchMerchants();
  }, []);
  useEffect(() => {
    const fetchColonies = async () => {
      try {
        const response = await adminServices.storeProcedure2("getColonies", {});
        if (response?.data && Array.isArray(response.data)) {
          const options = [{
            value: "",
            label: "Facility"
          }];
          response.data.forEach(colony => {
            if (colony.colony_name) {
              options.push({
                value: String(colony.colony_name),
                label: String(colony.colony_name)
              });
            }
          });
          setFacilities(options);
        } else {
          setFacilities([{
            value: "",
            label: "Facility"
          }]);
        }
      } catch (error) {
        console.error("Error fetching colonies:", error);
        setFacilities([{
          value: "",
          label: "Facility"
        }]);
      }
    };
    fetchColonies();
  }, []);

  // Fetch orders when filters change
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, dateRange, facility]);
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await adminServices.getOrderList({
        keyword: filters.keyword,
        merchantId: filters.merchantId,
        orderType: filters.orderType,
        dateFrom: dateRange.from || "",
        dateTo: dateRange.to || "",
        facility: facility || ""
      });
      setOrdersData(response.data || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };
  const fetchMerchants = async () => {
    try {
      const response = await adminServices.getInventoryMerchants("", 1, 1000);
      setMerchants(response.data || []);
    } catch (error) {
      console.error("Failed to fetch merchants:", error);
    }
  };
  const fetchOrderSummary = async () => {
    try {
      const response = await adminServices.getOrderSummary();
      if (response.data && response.data.length > 0) {
        setOrderSummary(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch order summary:", error);
    }
  };
  const handleSearchChange = e => {
    setSearchInput(e.target.value);
  };
  const handleSearchSubmit = e => {
    if (e.key === 'Enter') {
      setFilters(prev => ({
        ...prev,
        keyword: searchInput
      }));
    }
  };
  const handleMerchantChange = e => {
    const merchantId = e.target.value;
    setFilters(prev => ({
      ...prev,
      merchantId
    }));
  };
  const handleOrderTypeChange = e => {
    const orderType = e.target.value;
    setFilters(prev => ({
      ...prev,
      orderType
    }));
  };
  const handleExport = () => {
    if (!ordersData || ordersData.length === 0) {
      toast.warning("No data to export");
      return;
    }
    try {
      // Prepare CSV headers
      const headers = ["Date Issued", "Order ID", "Merchant Name", "Order Type", "Facilities", "Order Status", "Delivery ETA"];

      // Prepare CSV rows
      const rows = ordersData.map(row => [formatDateTime(row.dateIssued), row.orderId || "-", row.merchantName || "-", row.orderType || "-", row.facilities || "-", row.orderStatus || "-", row.deliveryEta ? formatDateTime(row.deliveryEta) : "-"]);

      // Combine headers and rows
      const csvContent = [headers.join(","), ...rows.map(row => row.map(cell => `"${cell}"`).join(","))].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;"
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `orders_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Orders exported successfully");
    } catch (error) {
      console.error("Failed to export:", error);
      toast.error("Failed to export orders");
    }
  };

  // Format date and time
  const formatDateTime = dateString => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${formattedDate} ${formattedTime}`;
  };

  // // Sample orders data
  // const ordersDataBase = [
  //   {
  //     id: 1,
  //     osNumber: "OS-000123",
  //     dateIssued: "2026-01-05",
  //     merchantName: "BrightWay Grocery",
  //     orderType: "Regular",
  //     facilities: "Quadrant 1",
  //     orderStatus: "Pending",
  //     remarks: "-",
  //     deliveryStatus: "-"
  //   },
  //   {
  //     id: 2,
  //     osNumber: "OS-000123",
  //     dateIssued: "2026-01-05",
  //     merchantName: "BrightWay Grocery",
  //     orderType: "Regular",
  //     facilities: "Quadrant 2",
  //     orderStatus: "Processing",
  //     remarks: "-",
  //     deliveryStatus: "-"
  //   },
  //   {
  //     id: 3,
  //     osNumber: "OS-000123",
  //     dateIssued: "2026-01-05",
  //     merchantName: "BrightWay Grocery",
  //     orderType: "Special",
  //     facilities: "Quadrant 3",
  //     orderStatus: "Pending",
  //     remarks: "from BuCor",
  //     deliveryStatus: "-"
  //   },
  //   {
  //     id: 4,
  //     osNumber: "OS-000123",
  //     dateIssued: "2026-01-05",
  //     merchantName: "BrightWay Grocery",
  //     orderType: "Special",
  //     facilities: "Maximum",
  //     orderStatus: "Approved",
  //     remarks: "from BuCor",
  //     deliveryStatus: "-"
  //   },
  //   {
  //     id: 5,
  //     osNumber: "OS-000123",
  //     dateIssued: "2026-01-05",
  //     merchantName: "BrightWay Grocery",
  //     orderType: "Special",
  //     facilities: "Medium",
  //     orderStatus: "Rejected",
  //     remarks: "from BuCor",
  //     deliveryStatus: "-"
  //   },
  //   {
  //     id: 6,
  //     osNumber: "OS-000123",
  //     dateIssued: "2026-01-05",
  //     merchantName: "BrightWay Grocery",
  //     orderType: "Regular",
  //     facilities: "RDC",
  //     orderStatus: "In Procurement",
  //     remarks: "-",
  //     deliveryStatus: "-"
  //   },
  //   {
  //     id: 7,
  //     osNumber: "OS-000123",
  //     dateIssued: "2026-01-05",
  //     merchantName: "BrightWay Grocery",
  //     orderType: "Special",
  //     facilities: "RDC",
  //     orderStatus: "Delivered",
  //     remarks: "from BuCor",
  //     deliveryStatus: "partially delivered"
  //   },
  //   {
  //     id: 8,
  //     osNumber: "OS-000123",
  //     dateIssued: "2026-01-05",
  //     merchantName: "BrightWay Grocery",
  //     orderType: "Special",
  //     facilities: "RDC",
  //     orderStatus: "Completed",
  //     remarks: "from BuCor",
  //     deliveryStatus: "partially delivered"
  //   }
  // ];

  // // Fill to minimum 10 rows
  // const ordersData = [...ordersDataBase];
  // const minRows = 10;
  // while (ordersData.length < minRows) {
  //   ordersData.push({
  //     id: ordersData.length + 1,
  //     osNumber: "",
  //     dateIssued: "",
  //     merchantName: "",
  //     orderType: "",
  //     facilities: "",
  //     orderStatus: "",
  //     remarks: "",
  //     deliveryStatus: ""
  //   });
  // }

  const getStatusColor = status => {
    switch (status) {
      case "PENDING":
        return {
          bg: "#fef3c7",
          color: "#92400e"
        };
      case "PROCESSING":
        return {
          bg: "#fef3c7",
          color: "#92400e"
        };
      case "APPROVED":
        return {
          bg: "#d1fae5",
          color: "#065f46"
        };
      case "REJECTED":
        return {
          bg: "#fce7f3",
          color: "#9f1239"
        };
      case "IN_PROCUREMENT":
        return {
          bg: "#fed7aa",
          color: "#9a3412"
        };
      case "DELIVERED":
        return {
          bg: "#dbeafe",
          color: "#1e40af"
        };
      case "COMPLETED":
        return {
          bg: "#d1fae5",
          color: "#065f46"
        };
      case "VOIDED":
        return {
          bg: "#fce7f3",
          color: "#9f1239"
        };
      default:
        return {
          bg: "#e5e7eb",
          color: "#374151"
        };
    }
  };
  const columns = [{
    name: "DATE ISSUED",
    selector: row => formatDateTime(row.dateIssued),
    sortable: true,
    width: "15%"
  }, {
    name: "ORDER ID",
    selector: row => row.orderId || "-",
    sortable: true,
    width: "15%"
  }, {
    name: "MERCHANT NAME",
    selector: row => row.merchantName || "-",
    sortable: true,
    width: "15%"
  }, {
    name: "ORDER TYPE",
    selector: row => row.orderType || "-",
    sortable: true,
    width: "11%"
  }, {
    name: "FACILITIES",
    selector: row => row.facilities || "-",
    sortable: true,
    width: "12%"
  }, {
    name: "ORDER STATUS",
    cell: row => {
      if (!row.orderStatus) return <div></div>;
      const statusColors = getStatusColor(row.orderStatus);
      return <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
            <span className="order-status-badge" style={{
          backgroundColor: statusColors.bg,
          color: statusColors.color
        }}>
              {row.orderStatus}
            </span>
          </div>;
    },
    sortable: true,
    width: "13%",
    center: true
  }, {
    name: "DELIVERY ETA",
    selector: row => row.deliveryEta ? formatDateTime(row.deliveryEta) : "-",
    sortable: true,
    width: "12%"
  }, {
    name: "Actions",
    cell: row => {
      if (!row.id) return <div></div>;
      return <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%"
      }}>
            <button className="order-view-link" style={{
          width: "auto",
          minWidth: "fit-content"
        }} onClick={() => {
          setSelectedOrderId(row.id);
          // If status is "Processing", go directly to ProcessOrderView
          if (row.orderStatus === "PROCESSING") {
            setShowProcessOrderView(true);
          } else {
            setShowViewOrderInformation(true);
          }
        }}>
              View
            </button>
          </div>;
    },
    sortable: false,
    width: "7%",
    center: true
  }];
  const customStyles = {
    table: {
      style: {
        tableLayout: 'fixed'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#002347",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "center"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }
    }
  };
  const summaryCardStyle = {
    background: "#ffffff",
    borderRadius: 14,
    padding: 16,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
  };
  const summaryTileStyle = {
    background: "#f9fafb",
    borderRadius: 10,
    padding: "12px 14px",
    minHeight: 72,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
  };
  return <div className="dashboard-content">
      {/* New Order Modal */}
      <NewOrderModal isOpen={isNewOrderModalOpen} onClose={() => {
      setIsNewOrderModalOpen(false);
    }} onProceed={data => {
      // Open OrderInformation page for both Regular and Special orders
      setOrderData(data);
      setIsNewOrderModalOpen(false);
      setShowOrderInformation(true);
    }} />

      {/* Process Order View */}
      {showProcessOrderView ? <ProcessOrderView orderId={selectedOrderId} onClose={() => {
      setShowProcessOrderView(false);
      setSelectedOrderId(null);
    }} /> : /* View Order Information Page */
    showViewOrderInformation ? <ViewOrderInformation orderId={selectedOrderId} onClose={() => {
      setShowViewOrderInformation(false);
      setSelectedOrderId(null);
    }} onProcessOrder={orderId => {
      setShowViewOrderInformation(false);
      setShowProcessOrderView(true);
    }} /> : /* Order Information Page */
    showOrderInformation ? <OrderInformation orderData={orderData} onClose={() => {
      setShowOrderInformation(false);
    }} onSubmit={async data => {
      const orderType = (data.orderData?.orderType ?? "Regular").toLowerCase();
      const items = data.specialItems?.length ? data.specialItems : data.regularItems ?? [];
      const orderItems = items.map(it => ({
        productId: Number(it.id ?? it.productId ?? 0),
        quantity: Number(it.quantity ?? 1)
      })).filter(it => it.productId > 0);
      const payload = {
        pdlNumber: String(data.pdlNumber ?? ""),
        merchantId: Number(data.orderData?.merchantId ?? 0),
        accountType: String(data.accountType ?? ""),
        category: String(data.category ?? "Market"),
        orderType,
        orderItems
      };
      try {
        await adminServices.createIpxOrder(payload);
        toast.success("Order created successfully.");
        setShowOrderInformation(false);
        fetchOrders(); // Refresh orders list
        fetchOrderSummary(); // Refresh summary
      } catch (err) {
        const responseData = err?.response?.data;
        const diagnosticPayload = {
          merchantId: payload.merchantId,
          pdlNumber: payload.pdlNumber,
          accountType: payload.accountType,
          category: payload.category,
          orderType: payload.orderType,
          orderItems: payload.orderItems
        };
        const diagnosticError = {
          message: err?.message ?? "Unknown error",
          status: err?.response?.status ?? null,
          statusText: err?.response?.statusText ?? "",
          data: responseData ?? null
        };
        const responseMessage = typeof responseData === "string" ? responseData : responseData?.message || responseData?.title || responseData?.error;
        const toastMessage = diagnosticError.status ? `Failed to create order (${diagnosticError.status}).${responseMessage ? ` ${responseMessage}` : ""}` : responseMessage || "Failed to create order.";
        console.error("Failed to create order", {
          payload: diagnosticPayload,
          error: diagnosticError
        });
        if (typeof window !== "undefined") {
          window.__lastCreateOrderDiagnostic = {
            at: new Date().toISOString(),
            payload: diagnosticPayload,
            error: diagnosticError
          };
        }
        toast.error(toastMessage);
      }
    }} /> : <>
          <div className="order-management-header">
            <div style={{
          display: "flex",
          alignItems: "center",
          gap: 12
        }}>
              <h1 className="dashboard-title" style={{
            margin: 0
          }}>Order Management</h1>
              <div className="dashboard-facility-dropdown">
                <select className="dashboard-facility-select" value={facility} onChange={e => setFacility(e.target.value)}>
                  {facilities.map(opt => <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>)}
                </select>
                <ChevronDown className="dashboard-facility-icon" size={18} />
              </div>
            </div>
            <button className="order-management-new-btn" onClick={() => setIsNewOrderModalOpen(true)}>
              <Plus size={18} />
              New Order
            </button>
          </div>

          <div style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
        marginBottom: 18
      }}>
            <label style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "#f3f4f6",
          borderRadius: 10,
          padding: "8px 10px",
          border: "1px solid #e5e7eb"
        }}>
              <span style={{
            fontSize: 12,
            color: "#374151"
          }}>From</span>
              <input type="date" value={dateRange.from} onChange={e => setDateRange(p => ({
            ...p,
            from: e.target.value
          }))} style={{
            border: "none",
            background: "transparent",
            fontSize: 12,
            outline: "none"
          }} />
            </label>
            <label style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "#f3f4f6",
          borderRadius: 10,
          padding: "8px 10px",
          border: "1px solid #e5e7eb"
        }}>
              <span style={{
            fontSize: 12,
            color: "#374151"
          }}>To</span>
              <input type="date" value={dateRange.to} onChange={e => setDateRange(p => ({
            ...p,
            to: e.target.value
          }))} style={{
            border: "none",
            background: "transparent",
            fontSize: 12,
            outline: "none"
          }} />
            </label>
          </div>
          
          {/* OS + PR Summary (UI match) */}
          <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 18,
        marginBottom: 18
      }}>
            <div style={summaryCardStyle}>
              <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: "#111827",
            marginBottom: 12
          }}>OS</div>
              <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12
          }}>
                <div style={summaryTileStyle}>
                  <div style={{
                fontSize: 12,
                color: "#6b7280"
              }}>Pending</div>
                  <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827"
              }}>{Number(orderSummary.pending ?? 0)}</div>
                </div>
                <div style={summaryTileStyle}>
                  <div style={{
                fontSize: 12,
                color: "#6b7280"
              }}>Rejected</div>
                  <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827"
              }}>{Number(orderSummary.rejected ?? 0)}</div>
                </div>
                <div style={summaryTileStyle}>
                  <div style={{
                fontSize: 12,
                color: "#6b7280"
              }}>Approved</div>
                  <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827"
              }}>{Number(orderSummary.approved ?? 0)}</div>
                </div>
                <div style={summaryTileStyle}>
                  <div style={{
                fontSize: 12,
                color: "#6b7280"
              }}>Voided</div>
                  <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827"
              }}>{Number(orderSummary.voided ?? 0)}</div>
                </div>
              </div>
            </div>

            <div style={summaryCardStyle}>
              <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: "#111827",
            marginBottom: 12
          }}>PR</div>
              <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12
          }}>
                <div style={summaryTileStyle}>
                  <div style={{
                fontSize: 12,
                color: "#6b7280"
              }}>Processing</div>
                  <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827"
              }}>{Number(orderSummary.processing ?? 0)}</div>
                </div>
                <div style={summaryTileStyle}>
                  <div style={{
                fontSize: 12,
                color: "#6b7280"
              }}>In Transit</div>
                  <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827"
              }}>{Number(orderSummary.inTransit ?? 0)}</div>
                </div>
                <div style={summaryTileStyle}>
                  <div style={{
                fontSize: 12,
                color: "#6b7280"
              }}>Partially Delivered</div>
                  <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827"
              }}>{Number(orderSummary.partiallyDelivered ?? 0)}</div>
                </div>
                <div style={summaryTileStyle}>
                  <div style={{
                fontSize: 12,
                color: "#6b7280"
              }}>Delivered</div>
                  <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#111827"
              }}>{Number(orderSummary.delivered ?? 0)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-management-table-section">
            <div className="table-wrapper">
              {/* Table Controls */}
              <div className="table-controls">
                <div className="table-controls-left">
                  <div className="search-input-container" style={{
                maxWidth: 620
              }}>
                    <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <input type="text" className="search-input" placeholder="Search" value={searchInput} onChange={handleSearchChange} onKeyDown={handleSearchSubmit} style={{
                  border: "1px solid #e5e7eb"
                }} />
                  </div>
                </div>
                <div className="table-controls-right" style={{
              gap: 5
            }}>
                  <select className="filter-select" value={filters.merchantId} onChange={handleMerchantChange}>
                    <option value="">All Merchants</option>
                    {merchants.map(merchant => <option key={merchant.merchantId} value={merchant.merchantId}>
                        {merchant.merchantName}
                      </option>)}
                  </select>
                  <select className="filter-select" value={filters.orderType} onChange={handleOrderTypeChange}>
                    <option value="">Order Type</option>
                    <option value="Regular">Regular</option>
                    <option value="Special">Special</option>
                  </select>
                  <button className="export-btn" onClick={handleExport}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Export
                  </button>
                </div>
              </div>

              <div className="order-management-table-container">
                <DataTable columns={columns} data={ordersData} customStyles={customStyles} striped highlightOnHover noHeader={false} pagination paginationPerPage={10} paginationRowsPerPageOptions={[10, 20, 30, 50]} progressPending={loading} progressComponent={<div>Loading orders...</div>} />
              </div>
            </div>
          </div>
        </>}
    </div>;
};
export default OrderManagement;